import dayjs from 'dayjs';
import type {UseFormReturn} from 'react-hook-form';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group';
import {TIME_FORMATS} from '@/utils/timeFormats';

import {
  MedicationScheduleMode,
  type ResidentMedicationBasicFormValues,
} from '../../utils/medicationFormInputs';
import DaysInput from '../DaysInput';
import type {DosageInputProps} from '../DosageInput';
import DosageInput from '../DosageInput';

export type MedicationScheduleFieldsProps = {
  form: UseFormReturn<ResidentMedicationBasicFormValues>;
  isTemporary: boolean;
  onAddDosageClick?: DosageInputProps['onAddDosageClick'];
  onDeleteDosageClick?: DosageInputProps['onDeleteDosageClick'];
  onEditDosageClick?: DosageInputProps['onEditDosageClick'];
};

function MedicationScheduleFields({
  form,
  isTemporary,
  onAddDosageClick,
  onDeleteDosageClick,
  onEditDosageClick,
}: MedicationScheduleFieldsProps) {
  const {t} = useTranslation();
  const scheduleMode =
    useWatch({
      control: form.control,
      name: 'scheduleMode',
    }) ?? MedicationScheduleMode.Daily;
  const isSpecificDays = scheduleMode === MedicationScheduleMode.SpecificDays;

  return (
    <>
      <FormField
        control={form.control}
        name="dosageDetails"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Dosages')}</FormLabel>
            <FormControl>
              <DosageInput
                onDeleteDosageClick={onDeleteDosageClick}
                onEditDosageClick={onEditDosageClick}
                onAddDosageClick={onAddDosageClick}
                value={field.value || []}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex gap-4 max-sm:flex-col">
        <FormField
          control={form.control}
          name="startDate"
          render={({field}) => (
            <FormItem className="grow basis-0">
              <FormLabel>{t('Start date')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={field.onChange}
                  value={
                    field.value
                      ? dayjs(field.value).format(TIME_FORMATS.awsDate)
                      : ''
                  }
                  type="date"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {isTemporary && (
          <FormField
            control={form.control}
            name="endDate"
            render={({field}) => (
              <FormItem className="grow basis-0">
                <FormLabel>{t('End date')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={field.onChange}
                    value={
                      field.value
                        ? dayjs(field.value).format(TIME_FORMATS.awsDate)
                        : ''
                    }
                    type="date"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      <FormField
        control={form.control}
        name="scheduleMode"
        render={({field}) => (
          <FormItem>
            <FormLabel>{t('Schedule')}</FormLabel>
            <FormControl>
              <ToggleGroup
                size="small"
                type="single"
                itemVariant="white"
                value={field.value || MedicationScheduleMode.Daily}
                onValueChange={value => {
                  if (!value) return;

                  const nextScheduleMode = value as MedicationScheduleMode;
                  field.onChange(nextScheduleMode);

                  if (nextScheduleMode === MedicationScheduleMode.Daily) {
                    form.setValue('daysOfWeek', [], {shouldValidate: true});
                  }
                }}
              >
                <ToggleGroupItem value={MedicationScheduleMode.Daily}>
                  {t('Daily')}
                </ToggleGroupItem>
                <ToggleGroupItem value={MedicationScheduleMode.SpecificDays}>
                  {t('Specific days')}
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {isSpecificDays && (
        <FormField
          control={form.control}
          name="daysOfWeek"
          render={({field}) => (
            <FormItem>
              <FormLabel required>{t('Days')}</FormLabel>
              <FormControl>
                <DaysInput
                  value={field.value || []}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}

export default MedicationScheduleFields;
